import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Handler from "../../../Helpers/Handler";
import "../../../Assets/css/style.css";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";

const PAGE_LIMIT = 10;

const StarDisplay = ({ rating }) => {
  return (
    <div className="rv-star-display">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`bx ${s <= Math.round(rating) ? "bxs-star rv-star--filled" : "bx-star rv-star--empty"}`}
        />
      ))}
      <span className="rv-rating-num">{parseFloat(rating).toFixed(1)}</span>
    </div>
  );
};

const ReviewList = () => {
  const currentUser = useSelector((s) => s.customer?.customer?.user);
  const isAdmin = currentUser?.role === "admin";

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Delete state
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const fetchList = useCallback(async (p = 1, q = "") => {
    setLoading(true);
    setError("");
    const res = await Handler({
      method: "GET",
      url: `/reviews/review_list.php?page=${p}&limit=${PAGE_LIMIT}&q=${encodeURIComponent(q)}`,
    });
    setLoading(false);
    if (res.success) {
      setList(res.data?.list || []);
      setTotal(res.data?.pagination?.total || 0);
    } else {
      setError(res.message || "Failed to fetch reviews.");
    }
  }, []);

  useEffect(() => {
    fetchList(page, search);
  }, [page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchList(1, search); }, 380);
    return () => clearTimeout(t);
  }, [search]);

  const handleDelete = async (review_id) => {
    setDeleting(true);
    setDeleteError("");
    const res = await Handler({
      method: "POST",
      url: "/reviews/delete.php",
      data: { review_id },
    });
    setDeleting(false);
    setDeleteId(null);
    if (res.success) {
      fetchList(page, search);
    } else {
      setDeleteError(res.message || "Failed to delete review.");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const getRankClass = (idx) => {
    if (page > 1) return "";
    if (idx === 0) return "rv-rank--gold";
    if (idx === 1) return "rv-rank--silver";
    if (idx === 2) return "rv-rank--bronze";
    return "";
  };

  return (
    <MainLayout>
      <FeedLayout>
        <div className="rv-page">

          {/* Header */}
          <div className="rv-page-header">
            <div>
              <h2 className="rv-page-title">Vendor Reviews</h2>
              <p className="rv-page-subtitle">Top rated vendors based on deals and events</p>
            </div>
          </div>

          {/* Alerts */}
          {error && <div className="rv-alert rv-alert--error"><i className="bx bx-error-circle" />{error}</div>}
          {deleteError && <div className="rv-alert rv-alert--error"><i className="bx bx-error-circle" />{deleteError}</div>}

          {/* Search */}
          <div className="rv-toolbar">
            <div className="rv-search-wrap">
              <i className="bx bx-search" />
              <input
                type="text"
                placeholder="Search vendor by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span className="rv-total-count">{total} vendor{total !== 1 ? "s" : ""} reviewed</span>
          </div>

          {/* List */}
          {loading ? (
            <div className="rv-spinner-wrap"><div className="rv-spinner" /></div>
          ) : list.length === 0 ? (
            <div className="rv-empty">
              <i className="bx bx-star" />
              <p>No reviews found.</p>
            </div>
          ) : (
            <div className="rv-vendor-list">
              {list.map((v, idx) => (
                <div key={v.vendor_id} className={`rv-vendor-card ${getRankClass(idx)}`}>

                  {/* Rank Badge */}
                  <div className={`rv-rank-badge ${getRankClass(idx)}`}>
                    {page === 1 && idx < 3
                      ? <i className="bx bxs-crown" />
                      : <span>#{(page - 1) * PAGE_LIMIT + idx + 1}</span>
                    }
                  </div>

                  {/* Avatar */}
                  <div className="rv-vendor-avatar">
                    {(v.name || "V").charAt(0).toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="rv-vendor-info">
                    <div className="rv-vendor-name">{v.name}</div>
                    <div className="rv-vendor-meta">
                      {v.mobile && <span><i className="bx bx-phone" />{v.mobile}</span>}
                      {v.email && <span><i className="bx bx-envelope" />{v.email}</span>}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="rv-vendor-stats">
                    <StarDisplay rating={v.avg_rating} />
                    <div className="rv-stat-row">
                      <span className="rv-stat-pill rv-stat-pill--blue">
                        <i className="bx bx-receipt" />{v.total_deals} Deals
                      </span>
                      <span className="rv-stat-pill rv-stat-pill--purple">
                        <i className="bx bx-calendar-event" />{v.total_events} Events
                      </span>
                      <span className="rv-stat-pill rv-stat-pill--gray">
                        <i className="bx bx-comment" />{v.total_reviews} Reviews
                      </span>
                    </div>
                  </div>

                  {/* Delete (admin only) */}
                  {isAdmin && (
                    <div className="rv-delete-wrap">
                      {deleteId === v.vendor_id ? (
                        <div className="rv-confirm-row">
                          <span className="rv-confirm-text">Delete all?</span>
                          <button
                            className="rv-btn-confirm-del"
                            onClick={() => handleDelete(v.vendor_id)}
                            disabled={deleting}
                          >
                            {deleting ? <i className="bx bx-loader-alt bx-spin" /> : "Yes"}
                          </button>
                          <button className="rv-btn-cancel-del" onClick={() => setDeleteId(null)}>No</button>
                        </div>
                      ) : (
                        <button className="rv-btn-del" onClick={() => setDeleteId(v.vendor_id)}>
                          <i className="bx bx-trash" />
                        </button>
                      )}
                    </div>
                  )}

                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && total > PAGE_LIMIT && (
            <div className="rv-pagination">
              <button
                className="rv-page-btn"
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
              >
                <i className="bx bx-chevron-left" /> Prev
              </button>
              <span className="rv-page-info">Page {page} of {totalPages}</span>
              <button
                className="rv-page-btn"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                Next <i className="bx bx-chevron-right" />
              </button>
            </div>
          )}

        </div>
      </FeedLayout>
    </MainLayout>

  );
};

export default ReviewList;